import { getLocale } from '@utils/contentful'
import {
  BIGCOMMERCE_CHANNEL_COUNTRY,
  fetchPlatformGraphqlApi,
} from '@utils/platform/common'
import { Entry } from 'contentful'
import flatMap from 'lodash/flatMap'
import { IProductCategory } from 'types/contentful/productCategory'
import uniqBy from 'lodash/uniqBy'
import { getVariantPrices } from './products'
import { PLUS_PRODUCT_TYPE } from 'app/(main)/(catalog)/_components/product/product.constants'
import parentLogger from '@utils/logger'
import { PageDataServiceFactory } from 'app/_services/CMS'

export const productQuery = `
  query($name: String!, $country: String!) {
    category(input: {filter: {name: $name, country: $country}}) {
      id
      name
      slug
      country
      recommendations {
        itemId
      }
    }
    categories(input: {filter: {slug: $country}}) {
      slug
      children {
        slug
        children {
          slug
          name
        }
      }
    }
  }
`

export async function queryForProductRecommendationsAndBigCommerceCategories(
  category: string
) {
  const response = await fetchPlatformGraphqlApi(productQuery, {
    variables: {
      name: category,
      country: BIGCOMMERCE_CHANNEL_COUNTRY.toLowerCase(),
    },
  })

  const productRecommendationsAsCategoryName =
    response?.data?.category?.recommendations?.map(
      (item) => item?.itemId
    ) as string[]

  const bigCommerceCategories = response?.data?.categories?.[0] as {
    children: { children: { name: string; slug: string } }[]
  }

  const flattenedBigCommerceCategories = flatMap(
    bigCommerceCategories?.children || [],
    (category) => category?.children
  )

  return {
    recommendations: productRecommendationsAsCategoryName || [],
    bigCommerceCategories: flattenedBigCommerceCategories || [],
  }
}

export async function getOverrideProducts(
  slugForOverrideProducts: string,
  preview: boolean
) {
  const logger = parentLogger.child({ name: 'getOverrideProducts' })

  try {
    const pageDataService = PageDataServiceFactory.createService()
    const overrideProductEntries = await pageDataService.getCrossSellOverride(
      slugForOverrideProducts
    )

    const crossSellOverrideProducts =
      overrideProductEntries?.items?.[0]?.fields?.crossSellOverrideProducts

    return crossSellOverrideProducts?.length ? crossSellOverrideProducts : []
  } catch (error) {
    logger.error(error, 'Error getting override products')
    return []
  }
}

export function mapRecommendationsToCategorySlug(
  productRecommendations: string[],
  bigcommerceCategories: { name: string; slug: string }[]
) {
  return bigcommerceCategories
    ?.map((category) => {
      if (productRecommendations?.includes(category?.name)) {
        return category.slug
      } else {
        return null
      }
    })
    .filter((x) => x)
}

export async function mapBCCategorySlugsToRecommendedProductType(
  bigCommerceCategorySlugs: string[] = [],
  preview: boolean
) {
  const pageDataService = PageDataServiceFactory.createService()

  let recommendedProductsInProductCategoryForm: Entry<IProductCategory>[]
  if (bigCommerceCategorySlugs?.length === 0) {
    recommendedProductsInProductCategoryForm =
      await pageDataService.getCrossSellOverride('default')
  } else {
    recommendedProductsInProductCategoryForm = (
      await pageDataService.getEntriesWithFetch({
        contentType: 'productCategory',
        query: `fields.productSlug[in]=${bigCommerceCategorySlugs.join(',')}`,
        include: 1,
      })
    )?.items as Entry<IProductCategory>[]
  }

  if (recommendedProductsInProductCategoryForm?.length) {
    return recommendedProductsInProductCategoryForm
  }

  return []
}

export function removeDuplicates(
  recommendedCategories: Entry<IProductCategory>[]
) {
  return uniqBy(recommendedCategories, (item) => {
    return item?.fields?.productSlug
  })
}

export async function filterOutCategoriesWithoutAvailableProductVariants(
  recommendedCategories: Entry<IProductCategory>[],
  take: number | undefined = undefined
) {
  const count =
    take > 0
      ? Math.min(take, recommendedCategories.length)
      : recommendedCategories.length

  const recommendedCategoriesWithProducts = []

  for (let i = 0; i < count; i++) {
    const recommendedProduct = recommendedCategories[i]
    if (recommendedProduct?.fields?.productType === PLUS_PRODUCT_TYPE) {
      recommendedCategoriesWithProducts.push(recommendedProduct)
    } else {
      const productVariants = await getVariantPrices(
        recommendedProduct?.fields?.productSlug
      )
      if (productVariants?.length > 0) {
        recommendedCategoriesWithProducts.push({
          ...recommendedProduct,
          productVariants,
        })
      }
    }
  }

  return recommendedCategoriesWithProducts.filter((x) => x)
}
